import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Container,
  TextField,
  InputAdornment,
  Chip,
  Typography,
  Card,
  CardMedia,
  CardContent,
  Grid,
  IconButton,
  Paper,
  Stack,
  CircularProgress,
  Button,
} from "@mui/material";
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  ArrowBack as ArrowBackIcon,
  LocationOn as LocationIcon,
} from "@mui/icons-material";
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  getDoc,
} from "firebase/firestore/lite";
import db from "../../firebase";

export const ActivitatCategoryPage = () => {
  interface activitatDB {
    id?: string;
    title?: string;
    desc?: string;
    price?: string;
    image?: string;
    docs?: Array<string>;
    contact?: {
      whats?: number;
      tel?: number;
      email?: string;
      link?: string;
    };
    address?: string;
    category?: string;
    subcategory?: string;
    top?: boolean;
    topOrder?: number;
    categoryOrder?: number;
    // parroquia?: string;
    // Per a qui: Sol / Parella / Amics / Família / Nens
    // Compartir
    // Disponibilitat: Tot l'any / Temporada específica
    // Durada: <1h / 1-3h / 3-6h / Tot el dia
    // Dificultat: Fàcil / Moderat / Difícil / Expert???
    // Tipus activitat: interior/exterior
  }

  const { categoryName } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const decodedCategoryName = decodeURIComponent(categoryName!);

  const [activities, setActivities] = useState<activitatDB[]>([]);
  const [subcategories, setSubcategories] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [selectedSubcategory, setSelectedSubcategory] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("" as string);

  console.log(decodedCategoryName);

  useEffect(() => {
    loadData();
  }, [location, decodedCategoryName]);

  console.log(activities);
  console.log(subcategories);

  const loadData = async () => {
    setLoading(true);
    setError("");

    try {
      // Carregar categories (subcategories)
      await loadCategories();

      // Carregar activitats
      await loadActivities();
    } catch (error) {
      console.error("Error loading data:", error);
      setError("Error carregant dades");
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      // 1. Prioritzar location.state
      if (location.state?.categories?.[decodedCategoryName]?.length > 0) {
        setSubcategories(location.state.categories[decodedCategoryName]);
        return;
      }

      // 2. Carregar de Firebase
      const categoriesSnapshot = await getDocs(collection(db, "categories"));

      if (!categoriesSnapshot.empty) {
        const allCategories = categoriesSnapshot.docs[0].data();
        setSubcategories(allCategories[decodedCategoryName] || []);
      } else {
        setSubcategories([]);
      }
    } catch (error) {
      console.error("Error loading categories:", error);
      setSubcategories([]);
    }
  };

  const loadActivities = async () => {
    try {
      // 1. Prioritzar location.state (més ràpid)
      if (location.state?.categoryActivities) {
        setActivities(location.state.categoryActivities);
        return;
      }

      // 2. Fallback: carregar de Firebase
      const q = query(
        collection(db, "activitats"),
        where("category", "==", decodedCategoryName)
      );
      const querySnapshot = await getDocs(q);
      const activitiesData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setActivities(activitiesData);
    } catch (error) {
      console.error("Error loading activities:", error);
      throw error;
    }
  };

  // Ordenació per categoryOrder
  const sortByCategory = (a: activitatDB, b: activitatDB) => {
    if (a.categoryOrder !== undefined && b.categoryOrder !== undefined) {
      return a.categoryOrder - b.categoryOrder;
    }
    if (a.categoryOrder !== undefined) return -1;
    if (b.categoryOrder !== undefined) return 1;
    return 0;
  };

  // Filtrar activitats
  const filteredActivities = useMemo(() => {
    let filtered = activities;

    // Filtrar per text
    if (searchText.trim()) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter((activity) => {
        const matchTitle = activity.title?.toLowerCase().includes(searchLower);
        const matchDesc = activity.desc?.toLowerCase().includes(searchLower);
        return matchTitle || matchDesc;
      });
    }

    // Filtrar per subcategoria
    if (selectedSubcategory) {
      filtered = filtered.filter(
        (activity) => activity.subcategory === selectedSubcategory
      );
    }

    return filtered.sort(sortByCategory);
  }, [activities, searchText, selectedSubcategory]);

  // Back navigation
  const handleBack = () => {
    // Comprovar si hi ha història de navegació dins l'app
    // location.key === 'default' indica que s'ha entrat directament amb la URL
    if (location.key !== "default") {
      navigate(-1); // Tornar a la pàgina anterior
    } else {
      navigate("/activitats"); // Si no hi ha història, anar a la pàgina principal
    }
  };

  // Renderitzar card d'activitat
  const ActivityCard = ({ activity }: { activity: activitatDB }) => (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        cursor: "pointer",
        transition: "transform 0.2s, box-shadow 0.2s",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: 4,
        },
      }}
      onClick={() =>
        navigate(`/activitats/${activity.id}`, {
          state: { activity }, // ← Passar l'objecte complet
        })
      }
    >
      <CardMedia
        component="img"
        height="200"
        image={activity.image || "https://via.placeholder.com/400x200"}
        alt={activity.title}
        sx={{ objectFit: "cover" }}
      />
      <CardContent sx={{ flexGrow: 1 }}>
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          {activity.subcategory && (
            <Chip
              label={activity.subcategory}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ fontSize: "0.7rem" }}
            />
          )}
        </Box>

        <Typography variant="h6" gutterBottom>
          {activity.title}
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mb: 2,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {activity.desc}
        </Typography>

        <Box display="flex" alignItems="center" gap={0.5} mb={1}>
          <LocationIcon sx={{ fontSize: 16, color: "text.secondary" }} />
          <Typography variant="body2" color="text.secondary" noWrap>
            {activity.address}
          </Typography>
        </Box>

        <Typography variant="body2" fontWeight="600" color="primary">
          {activity.price}
        </Typography>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor="#F8F9FA"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box textAlign="center">
          <Typography variant="h5" color="error" gutterBottom>
            {error}
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate("/activitats")}
            sx={{ mt: 2 }}
          >
            Tornar a activitats
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Box sx={{ bgcolor: "#F8F9FA", minHeight: "100vh", py: 4 }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <IconButton onClick={handleBack}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" sx={{ fontWeight: "bold" }}>
            {decodedCategoryName}
          </Typography>
        </Box>

        {/* Filtres */}
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          {/* Cercador */}
          <TextField
            fullWidth
            placeholder="Cerca per nom..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: (searchText || selectedSubcategory) && (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => {
                      setSearchText("");
                      setSelectedSubcategory("");
                    }}
                    size="small"
                  >
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ mb: subcategories.length > 0 ? 2 : 0 }}
          />

          {/* Filtres de subcategoria (només si n'hi ha) */}
          {subcategories.length > 0 && (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip
                label="Totes"
                onClick={() => setSelectedSubcategory("")}
                color={selectedSubcategory === "" ? "primary" : "default"}
                variant={selectedSubcategory === "" ? "filled" : "outlined"}
              />
              {subcategories.map((subcategory) => (
                <Chip
                  key={subcategory}
                  label={subcategory}
                  onClick={() =>
                    setSelectedSubcategory(
                      selectedSubcategory === subcategory ? "" : subcategory
                    )
                  }
                  color={
                    selectedSubcategory === subcategory ? "primary" : "default"
                  }
                  variant={
                    selectedSubcategory === subcategory ? "filled" : "outlined"
                  }
                />
              ))}
            </Stack>
          )}

          {/* Indicador de resultats */}
          <Box mt={2}>
            <Typography variant="body2" color="text.secondary">
              {filteredActivities.length} activitat
              {filteredActivities.length !== 1 ? "s" : ""} trobada
              {filteredActivities.length !== 1 ? "es" : ""}
            </Typography>
          </Box>
        </Paper>

        {/* Llistat d'activitats */}
        {filteredActivities.length > 0 ? (
          <Grid container spacing={3}>
            {filteredActivities.map((activity) => (
              <Grid item xs={12} sm={6} md={4} key={activity.id}>
                <ActivityCard activity={activity} />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Paper elevation={2} sx={{ p: 6, textAlign: "center" }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No s'han trobat activitats
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Prova amb una altra cerca o filtre
            </Typography>
          </Paper>
        )}
      </Container>
    </Box>
  );
};
